/**
 * VideoAudioTrack - Displays audio waveforms for video clips as a separate track below the video track
 * This track mirrors the video clips but shows their embedded audio content
 */
import { useCallback, useMemo } from 'react';
import { Volume2 } from 'lucide-react';
import { getEffectiveDuration } from '../utils/clipUtils';
import { useWaveform } from '../hooks/useWaveform';

// Waveform component that fetches and displays real audio data
// Shows the waveform at its actual duration, not stretched to fill the clip
function AudioWaveform({ audioUrl, clipWidth, clipDurationMs, trimStartMs = 0, trimEndMs = 0, totalDurationMs, getAssetUrl }) {
  const fullUrl = audioUrl && getAssetUrl ? getAssetUrl(audioUrl) : null;
  // Get enough samples for good resolution
  const baseSamples = Math.max(Math.floor(clipWidth / 2), 50);
  const { waveformData, loading, duration: audioDuration } = useWaveform(fullUrl, baseSamples * 2);

  // Calculate the actual width the waveform should occupy based on audio vs clip duration
  const { displayWidth, trimmedWaveform } = useMemo(() => {
    if (!waveformData || waveformData.length === 0) {
      return { displayWidth: clipWidth, trimmedWaveform: null };
    }
    
    // Get actual audio duration (from the waveform hook or fall back to clip's total duration)
    const actualAudioDuration = audioDuration || totalDurationMs || clipDurationMs;
    
    // Calculate trimmed audio duration
    const trimmedAudioStart = trimStartMs;
    const trimmedAudioEnd = Math.min(actualAudioDuration, totalDurationMs - trimEndMs);
    const trimmedAudioDuration = Math.max(0, trimmedAudioEnd - trimmedAudioStart);
    
    // Calculate what portion of the clip width the actual audio should occupy
    // If audio is shorter than the clip, don't stretch it
    const audioWidthRatio = Math.min(1, trimmedAudioDuration / clipDurationMs);
    const actualDisplayWidth = clipWidth * audioWidthRatio;
    
    // Slice the waveform to show only the trimmed portion
    const totalSamples = waveformData.length;
    if (actualAudioDuration > 0) {
      const startRatio = trimStartMs / actualAudioDuration;
      const endRatio = Math.min(1, trimmedAudioEnd / actualAudioDuration);
      
      const startIndex = Math.floor(startRatio * totalSamples);
      const endIndex = Math.ceil(endRatio * totalSamples);
      
      return {
        displayWidth: actualDisplayWidth,
        trimmedWaveform: waveformData.slice(
          Math.max(0, startIndex),
          Math.min(totalSamples, endIndex)
        )
      };
    }
    
    return { displayWidth: actualDisplayWidth, trimmedWaveform: waveformData };
  }, [waveformData, audioDuration, totalDurationMs, clipDurationMs, clipWidth, trimStartMs, trimEndMs]);

  // Check if waveform is essentially silent (below threshold)
  const isSilent = useMemo(() => {
    if (!trimmedWaveform || trimmedWaveform.length === 0) return true;
    
    // Calculate average and max amplitude
    const sum = trimmedWaveform.reduce((acc, val) => acc + val, 0);
    const avg = sum / trimmedWaveform.length;
    const max = Math.max(...trimmedWaveform);
    
    // Treat as silent if average < 5% OR max < 10%
    return avg < 0.05 || max < 0.1;
  }, [trimmedWaveform]);

  // Show loading state
  if (loading && fullUrl) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="w-3 h-3 border-2 border-green-400 border-t-transparent rounded-full animate-spin opacity-60" />
      </div>
    );
  }

  // Show real waveform if available and not silent
  if (trimmedWaveform && trimmedWaveform.length > 0 && !isSilent) {
    return (
      <div className="w-full h-full flex items-start overflow-hidden px-0.5">
        {/* Waveform container - only takes up the actual audio duration width */}
        <div style={{ width: displayWidth, height: '100%', flexShrink: 0 }}>
          <svg 
            className="w-full h-full" 
            preserveAspectRatio="none"
            viewBox={`0 0 ${trimmedWaveform.length} 1`}
          >
            {trimmedWaveform.map((height, i) => (
              <rect
                key={i}
                x={i}
                y={(1 - height) / 2}
                width={0.7}
                height={Math.max(height, 0.05)}
                fill="#22c55e"
                opacity={0.8}
              />
            ))}
          </svg>
        </div>
        {/* Remaining space is empty (silent) */}
      </div>
    );
  }

  // No waveform data or silent - show "no audio" indicator
  return (
    <div className="w-full h-full flex items-center justify-center opacity-40">
      <span className="text-[9px] text-zinc-500 tracking-wide">NO AUDIO</span>
    </div>
  );
}

export function VideoAudioTrack({
  clips = [],
  shots = [],
  totalWidth,
  labelWidth = 120,
  pxPerMs,
  selectedClipIds = [],
  onSelectClip,
  getAssetUrl
}) {
  // Get shot data for a clip
  const getShotForClip = useCallback((clip) => {
    if (!clip || clip.is_blank) return null;
    return shots.find(s => s.id === clip.scene_id);
  }, [shots]);

  // Get take data for a clip
  const getTakeForClip = useCallback((clip, shot) => {
    if (!shot) return null;
    const takes = shot.takes || [];
    if (takes.length === 0) return null;
    
    // Priority: printed_take_id > clip.take_id > first take with video
    if (shot.printed_take_id) {
      const take = takes.find(t => t.id === shot.printed_take_id);
      if (take) return take;
    }
    if (clip.take_id) {
      const take = takes.find(t => t.id === clip.take_id);
      if (take) return take;
    }
    return takes.find(t => t.video_url) || takes[0];
  }, []);

  // Get audio URL for a clip (from video or separate audio file)
  const getAudioUrl = useCallback((clip, shot, take) => {
    if (!take) return null;
    // Prefer separate audio file, fall back to video URL (which contains embedded audio)
    return take.audio_url || take.voice_audio_url || take.video_url;
  }, []);

  return (
    <div 
      className="h-14 bg-zinc-900/50 border-b border-zinc-800 flex shrink-0"
    >
      {/* Track label - sticky on left */}
      <div 
        className="shrink-0 border-r border-zinc-800 flex items-center px-3 bg-zinc-900/80 sticky left-0 z-10"
        style={{ width: labelWidth }}
      >
        <Volume2 className="w-4 h-4 text-green-400 mr-2" />
        <span className="text-xs text-zinc-400">Audio</span>
      </div>
      
      {/* Audio clips area - mirrors video clips */}
      <div 
        className="relative flex-1 overflow-hidden track-background"
        style={{ width: totalWidth }}
      >
        {clips?.map((clip, index) => {
          const shot = getShotForClip(clip);
          const take = getTakeForClip(clip, shot);
          const audioUrl = getAudioUrl(clip, shot, take);
          const isSelected = selectedClipIds?.includes(clip.id);
          const hasAudio = !!audioUrl && !clip.is_blank;
          
          // Calculate effective duration (matches video clip)
          const effectiveDuration = getEffectiveDuration(clip);
          const clipWidth = effectiveDuration * pxPerMs;
          const clipLeft = clip.start_ms * pxPerMs;
          
          // Skip blank clips or clips without audio
          if (clip.is_blank) {
            return (
              <div
                key={clip.id}
                className="absolute top-1 bottom-1 rounded bg-zinc-800/50 border border-dashed border-zinc-700"
                style={{
                  left: clipLeft,
                  width: Math.max(clipWidth, 4)
                }}
              />
            );
          }
          
          return (
            <div
              key={clip.id}
              className={`absolute top-1 bottom-1 rounded transition-shadow select-none overflow-hidden ${
                isSelected
                  ? 'ring-2 ring-white shadow-lg z-10'
                  : 'hover:ring-1 hover:ring-zinc-500'
              } ${
                hasAudio
                  ? 'bg-green-900/40'
                  : 'bg-zinc-800/60'
              }`}
              style={{
                left: clipLeft,
                width: Math.max(clipWidth, 4)
              }}
              onClick={(e) => {
                e.stopPropagation();
                onSelectClip?.(clip.id, 'video', e.shiftKey || e.metaKey || e.ctrlKey);
              }}
              data-testid={`video-audio-clip-${index}`}
            >
              {hasAudio && clipWidth > 20 ? (
                <AudioWaveform 
                  audioUrl={audioUrl}
                  clipWidth={clipWidth}
                  clipDurationMs={effectiveDuration}
                  trimStartMs={clip.trim_start_ms || 0}
                  trimEndMs={clip.trim_end_ms || 0}
                  totalDurationMs={clip.duration_ms}
                  getAssetUrl={getAssetUrl}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center opacity-40">
                  <Volume2 className="w-3 h-3 text-zinc-500" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
